<template>
  <div class="bg-design-container" :style="backgroundStyle">
    <div v-if="settings.patternEnabled && settings.patternType !== 'night-sky'" class="pattern-overlay" :style="patternStyle"></div>
    <div v-if="settings.patternEnabled && (settings.patternType === 'polka' || settings.patternType === 'heart')" class="pattern-overlay pattern-overlay-shifted" :style="patternShiftedStyle"></div>
    <div v-if="settings.patternEnabled && settings.patternType === 'night-sky'" class="night-sky-container">
      <div class="stars stars-sm" :style="starsSmStyle"></div>
      <div class="stars stars-md" :style="starsMdStyle"></div>
      <div class="stars stars-lg" :style="starsLgStyle"></div>
    </div>
  </div>
</template>

<script>
const HEART_SVG_PATH = "M384,46.469c-70.688,0-128,57.313-128,128.016c0-70.703-57.313-128.016-128-128.016S0,103.781,0,174.484c0,66.484,31.313,193.391,218.563,276.234c11.844,5.25,35.703,14.469,35.703,14.469c0.547,0.219,1.141,0.344,1.734,0.344s1.188-0.125,1.734-0.344c0,0,23.859-9.219,35.703-14.469C480.688,367.875,512,240.969,512,174.484C512,103.781,454.688,46.469,384,46.469z"

export default {
  name: 'BgDesign',
  data() {
    return {
      settings: {
        bgType: 'gradient',
        bgColor1: '#D9AFD9',
        bgColor2: '#97D9E1',
        gradientDirection: 'to bottom right',
        patternEnabled: true,
        patternType: 'polka',
        patternColor: '#ffffff',
        patternSize: 5,
        patternSpacing: 25,
        patternOpacity: 16
      },
      starsSmStyle: {},
      starsMdStyle: {},
      starsLgStyle: {}
    }
  },
  computed: {
    backgroundStyle() {
      let style = {
        width: '100vw',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        overflow: 'hidden'
      }
      
      if (this.settings.bgType === 'gradient') {
        style.background = `linear-gradient(${this.settings.gradientDirection}, ${this.settings.bgColor1}, ${this.settings.bgColor2})`
      } else {
        style.backgroundColor = this.settings.bgColor1
      }
      
      return style
    },
    patternStyle() {
      const opacity = this.settings.patternOpacity / 100
      const color = this.hexToRgba(this.settings.patternColor, opacity)
      let backgroundImage = ''
      let backgroundSize = ''
      let backgroundPosition = '0 0'
      
      switch (this.settings.patternType) {
        case 'polka':
          backgroundImage = `radial-gradient(circle, ${color} ${this.settings.patternSize / 2}px, transparent ${this.settings.patternSize / 2}px)`
          backgroundSize = `${this.settings.patternSpacing}px ${this.settings.patternSpacing}px`
          break
        case 'heart': {
          const heartSvgUrl = this.generateHeartSvg(opacity)
          backgroundImage = `url("${heartSvgUrl}")`
          backgroundSize = `${this.settings.patternSpacing}px ${this.settings.patternSpacing}px`
          break
        }
        case 'stripe-vertical':
          backgroundImage = `repeating-linear-gradient(to right, transparent, transparent ${this.settings.patternSpacing - this.settings.patternSize}px, ${color} ${this.settings.patternSpacing - this.settings.patternSize}px, ${color} ${this.settings.patternSpacing}px)`
          backgroundSize = `${this.settings.patternSpacing}px 100%`
          break
        case 'stripe-horizontal':
          backgroundImage = `repeating-linear-gradient(to bottom, transparent, transparent ${this.settings.patternSpacing - this.settings.patternSize}px, ${color} ${this.settings.patternSpacing - this.settings.patternSize}px, ${color} ${this.settings.patternSpacing}px)`
          backgroundSize = `100% ${this.settings.patternSpacing}px`
          break
        case 'stripe-diagonal':
          backgroundImage = `repeating-linear-gradient(45deg, ${color}, ${color} ${this.settings.patternSize}px, transparent ${this.settings.patternSize}px, transparent ${this.settings.patternSpacing}px)`
          backgroundSize = 'auto'
          break
        case 'check': {
          const checkSize = this.settings.patternSize
          const checkColor = color
          backgroundImage = `
            linear-gradient(${checkColor} ${checkSize}px, transparent ${checkSize}px),
            linear-gradient(90deg, ${checkColor} ${checkSize}px, transparent ${checkSize}px)
          `
          backgroundSize = `${checkSize * 2}px ${checkSize * 2}px`
          break
        }
        case 'grid': {
          const gridSize = this.settings.patternSpacing
          const lineWidth = this.settings.patternSize
          const gridColor = color
          backgroundImage = `
            linear-gradient(${gridColor} ${lineWidth}px, transparent ${lineWidth}px),
            linear-gradient(90deg, ${gridColor} ${lineWidth}px, transparent ${lineWidth}px)
          `
          backgroundSize = `${gridSize}px ${gridSize}px`
          break
        }
        case 'night-sky':
          // night-skyは特別な処理が必要なのでここでは何もしない
          break
      }
      
      if (this.settings.patternType === 'night-sky') {
        return {}
      }
      
      return {
        backgroundImage: backgroundImage.trim(),
        backgroundSize: backgroundSize,
        backgroundPosition: backgroundPosition,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }
    },
    patternShiftedStyle() {
      if (this.settings.patternType === 'night-sky') {
        return {}
      }

      if (this.settings.patternType !== 'polka' && this.settings.patternType !== 'heart') return {}
      
      const opacity = this.settings.patternOpacity / 100
      const color = this.hexToRgba(this.settings.patternColor, opacity)
      const shift = this.settings.patternSpacing / 2
      
      if (this.settings.patternType === 'polka') {
        return {
          backgroundImage: `radial-gradient(circle, ${color} ${this.settings.patternSize / 2}px, transparent ${this.settings.patternSize / 2}px)`,
          backgroundSize: `${this.settings.patternSpacing}px ${this.settings.patternSpacing}px`,
          backgroundPosition: `${shift}px ${shift}px`,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1
        }
      }
      
      if (this.settings.patternType === 'heart') {
        const heartSvgUrl = this.generateHeartSvg(opacity)
        return {
          backgroundImage: `url("${heartSvgUrl}")`,
          backgroundSize: `${this.settings.patternSpacing}px ${this.settings.patternSpacing}px`,
          backgroundPosition: `${shift}px ${shift}px`,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1
        }
      }
      
      return {}
    }
  },
  mounted() {
    this.loadSettings()
    if (this.settings.patternType === 'night-sky') {
      this.generateNightSky()
    }
  },
  methods: {
    loadSettings() {
      // URLパラメータから設定を読み込む
      const params = new URLSearchParams(window.location.search)
      if (params.has('bgType')) {
        this.settings.bgType = params.get('bgType') || this.settings.bgType
        this.settings.bgColor1 = params.get('bgColor1') || this.settings.bgColor1
        this.settings.bgColor2 = params.get('bgColor2') || this.settings.bgColor2
        this.settings.gradientDirection = params.get('gradientDirection') || this.settings.gradientDirection
        this.settings.patternEnabled = params.get('patternEnabled') === 'true'
        this.settings.patternType = params.get('patternType') || this.settings.patternType
        this.settings.patternColor = params.get('patternColor') || this.settings.patternColor
        const patternSizeParam = parseInt(params.get('patternSize'))
        this.settings.patternSize = !isNaN(patternSizeParam) ? patternSizeParam : this.settings.patternSize
        const patternSpacingParam = parseInt(params.get('patternSpacing'))
        this.settings.patternSpacing = !isNaN(patternSpacingParam) ? patternSpacingParam : this.settings.patternSpacing
        const patternOpacityParam = parseInt(params.get('patternOpacity'))
        this.settings.patternOpacity = !isNaN(patternOpacityParam) ? patternOpacityParam : this.settings.patternOpacity
        
        if (this.settings.patternType === 'night-sky') {
          this.generateNightSky()
        }
      } else {
        // URLパラメータがない場合は、localStorageから読み込む（後方互換性）
        const saved = localStorage.getItem('bgGeneratorSettings')
        if (saved) {
          try {
            this.settings = JSON.parse(saved)
            // 後方互換性のため、古い設定名を新しい設定名に変換
            if (this.settings.polkaEnabled !== undefined && this.settings.patternEnabled === undefined) {
              this.settings.patternEnabled = this.settings.polkaEnabled
              this.settings.patternType = 'polka'
              this.settings.patternColor = this.settings.polkaColor || '#ffffff'
              this.settings.patternSize = this.settings.polkaSize || 5
              this.settings.patternSpacing = this.settings.polkaSpacing || 25
              this.settings.patternOpacity = this.settings.polkaOpacity || 16
            }
            if (this.settings.patternType === 'night-sky') {
              this.generateNightSky()
            }
          } catch (e) {
            console.error('設定の読み込みに失敗しました', e)
          }
        }
      }
    },
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    generateNightSky() {
      const density = Math.max(10, this.settings.patternSpacing) 
      const baseCount = density * 3 
      
      const baseColor = this.settings.patternColor
      const opacity = this.settings.patternOpacity / 100
      const baseSize = Math.max(1, this.settings.patternSize / 5)

      this.starsSmStyle = this.createStarLayer(baseCount * 3, 1 * baseSize, baseColor, opacity * 0.8)
      this.starsMdStyle = this.createStarLayer(baseCount * 1.5, 2 * baseSize, baseColor, opacity)
      this.starsLgStyle = this.createStarLayer(baseCount * 0.5, 3 * baseSize, baseColor, opacity)
    },
    createStarLayer(count, size, hexColor, opacity) {
      let boxShadow = ''
      const r = parseInt(hexColor.slice(1, 3), 16)
      const g = parseInt(hexColor.slice(3, 5), 16)
      const b = parseInt(hexColor.slice(5, 7), 16)
      
      for (let i = 0; i < count; i++) {
        const alpha = (Math.random() * 0.5 + 0.5) * opacity 
        const color = `rgba(${r}, ${g}, ${b}, ${alpha})`
        const blur = size > 1.5 ? Math.random() * 2 + 1 : 0
        boxShadow += `${Math.random() * 100}vw ${Math.random() * 100}vh ${blur}px ${size/2}px ${color}, `
      }
      
      boxShadow = boxShadow.slice(0, -2)
      
      return {
        boxShadow: boxShadow,
        width: '1px',
        height: '1px',
        background: 'transparent',
        position: 'fixed',
        opacity: 1
      }
    },
    generateHeartSvg(fillOpacity) {
      const spacing = Math.max(1, this.settings.patternSpacing)
      const heartScale = this.getHeartScale()
      const transform = `translate(256 256) scale(${heartScale}) translate(-256 -256)`
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${spacing}' height='${spacing}' viewBox='0 0 512 512'><path d='${HEART_SVG_PATH}' fill='${this.settings.patternColor}' fill-opacity='${fillOpacity}' transform='${transform}'/></svg>`
      return `data:image/svg+xml,${encodeURIComponent(svg)}`
    },
    getHeartScale() {
      const spacing = Math.max(1, this.settings.patternSpacing)
      const normalized = this.settings.patternSize / spacing
      return Math.min(1.1, Math.max(0.2, normalized * 2))
    }
  }
}
</script>

<style scoped>
.bg-design-container {
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  overflow: hidden;
}

@keyframes animStar {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-100vh);
  }
}

/* 星空のスタイル */
.night-sky-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  perspective: 1000px;
}

.stars {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.stars-sm {
  animation: animStar 150s linear infinite;
}
.stars-sm:after {
  content: " ";
  position: absolute;
  top: 100vh;
  width: 1px;
  height: 1px;
  background: transparent;
  box-shadow: inherit;
}

.stars-md {
  animation: animStar 100s linear infinite;
}
.stars-md:after {
  content: " ";
  position: absolute;
  top: 100vh;
  width: 1px;
  height: 1px;
  background: transparent;
  box-shadow: inherit;
}

.stars-lg {
  animation: animStar 50s linear infinite;
}
.stars-lg:after {
  content: " ";
  position: absolute;
  top: 100vh;
  width: 1px;
  height: 1px;
  background: transparent;
  box-shadow: inherit;
}

@keyframes twinkle {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}
</style>

